import React, { useState } from 'react';
import { differenceInDays, format } from 'date-fns';
import { ExperimentStatus } from 'src/services';
import {
  ActionIcon,
  AvailableIcons,
  Button,
  ConfirmationDialog,
  Typography,
  TypographyType,
  TypographyVariant,
} from 'src/components-dummy';
import classNames from 'classnames';
import { formatSignedPercent, getDeltaClassName } from 'src/utils';
import { ButtonsContainer } from 'src/components-dummy/ConfirmationDialog/ConfirmationDialog.style';
import { CardItem } from 'src/components-dummy/CardItem';
import { useExperimentCard } from './useExperimentCard';
import { ExperimentCardProps } from './types';
import { experimentMetricTranslations } from '../constants';
import './ExperimentCard.scss';
import { getExperimentStatusClass } from '../experiment-helpers';
import { experimentActionButtonsActionMethods } from '../ExperimentActionButtons/Actions';
import { UseUuidModal } from './UseUuidModal';

export const ExperimentCard = ({
  experiment,
  runningExperimentId,
  dispatch,
  shopId,
  experimentFormRoute,
  onViewResults,
}: ExperimentCardProps): JSX.Element => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [showUseUuidDialog, setShowUseUuidDialog] = useState(false);
  const { onEditExperiment, onDeleteExperiment, onDeleteDialogClose, onRunExperiment } =
    useExperimentCard({
      experiment,
      runningExperimentId,
      dispatch,
      shopId,
      experimentFormRoute,
      setShowUseUuidDialog,
    });

  const isCreated = experiment.status === ExperimentStatus.Created;
  const isRunning = experiment.status === ExperimentStatus.Running;
  const isEnded = experiment.status === ExperimentStatus.Ended;
  const isProvisioning = experiment.status === ExperimentStatus.Provisioning;

  const daysInTest =
    experiment.analyticsSummary?.daysInTest ??
    (isRunning
      ? experiment.startedAt
        ? differenceInDays(new Date(), experiment.startedAt)
        : 0
      : experiment.startedAt && experiment.endedAt
        ? differenceInDays(experiment.endedAt, experiment.startedAt)
        : 0);

  const totalUsers =
    experiment.analyticsSummary?.variantSummary?.reduce((sum, v) => sum + v.users, 0) ?? 0;

  const winnerStatus = (() => {
    const state = experiment.analyticsSummary?.state;
    if (!state) return { text: '—', className: '' };
    switch (state) {
      case 'winner_found':
        return { text: 'Winner found', className: 'winner-status-green' };
      case 'collecting_data':
        return { text: 'Collecting data', className: 'winner-status-gray' };
      case 'inconclusive':
        return { text: 'No clear winner', className: 'winner-status-gray' };
      case 'ended_winner':
        return { text: 'Winner', className: 'winner-status-green' };
      case 'ended_inconclusive':
        return { text: 'No winner', className: 'winner-status-red' };
      default:
        return { text: '—', className: '' };
    }
  })();

  const isCollectingData = experiment.analyticsSummary?.state === 'collecting_data';
  const liftValue =
    isCollectingData || experiment.analyticsSummary?.deltas?.cvr == null
      ? null
      : experiment.analyticsSummary.deltas.cvr;
  const liftText = liftValue != null ? formatSignedPercent(liftValue) : '—';
  const liftClassName =
    liftValue != null ? getDeltaClassName(liftValue, 'lift-positive', 'lift-negative') : '';

  const nameMeta = isProvisioning
    ? experiment.provisioningError
      ? 'Setting up failed'
      : 'Setting up...'
    : isCreated
      ? `Not started · created ${format(experiment.createdAt, 'dd-MM-yyyy')}`
      : isEnded
        ? `Ran ${daysInTest} days · ${totalUsers.toLocaleString()} users`
        : `Day ${daysInTest} of test · ${totalUsers.toLocaleString()} users`;

  const onStopExperiment = () => {
    dispatch(
      experimentActionButtonsActionMethods.stopExperiment({ shopId, id: experiment.id, experiment })
    );
  };

  const isStartEnabled =
    isCreated && (!runningExperimentId || runningExperimentId === experiment.id);

  const actionButtons = (
    <div className='syte-experiment-card-actions' onClick={e => e.stopPropagation()}>
      {isCreated && (
        <>
          <Button
            variant='primary'
            disabled={!isStartEnabled}
            onClick={() => setShowUseUuidDialog(true)}
          >
            Start
          </Button>
          <Button variant='secondary' onClick={onEditExperiment}>
            Edit
          </Button>
          <ActionIcon
            iconName={AvailableIcons.TrashCan}
            onClick={() => setShowDeleteDialog(true)}
          />
        </>
      )}
      {isProvisioning && (
        <>
          <Button variant='secondary' onClick={onEditExperiment}>
            Open
          </Button>
          <ActionIcon
            iconName={AvailableIcons.TrashCan}
            onClick={() => setShowDeleteDialog(true)}
          />
        </>
      )}
      {isRunning && (
        <>
          {onViewResults && (
            <Button variant='primary' onClick={() => onViewResults(experiment.id)}>
              View results →
            </Button>
          )}
          {showStopDialog ? (
            <>
              <Button
                variant='primary'
                onClick={() => {
                  onStopExperiment();
                  setShowStopDialog(false);
                }}
              >
                Confirm
              </Button>
              <Button variant='outlined' onClick={() => setShowStopDialog(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button variant='secondary' onClick={() => setShowStopDialog(true)}>
              Stop
            </Button>
          )}
        </>
      )}
      {isEnded && (
        <>
          {onViewResults && (
            <Button variant='primary' onClick={() => onViewResults(experiment.id)}>
              View results →
            </Button>
          )}
          <Button variant='secondary' onClick={onEditExperiment}>
            View config
          </Button>
        </>
      )}
    </div>
  );

  return (
    <CardItem className='syte-experiment-card'>
      <div
        className={classNames(
          'syte-experiment-card-status',
          getExperimentStatusClass(experiment.status)
        )}
      >
        <Typography
          type={TypographyType.Body}
          variant={TypographyVariant.SmallRegular}
          className='syte-experiment-card-status-text'
        >
          {experiment.status}
        </Typography>
      </div>
      <div className='syte-experiment-card-content'>
        <div className='syte-experiment-card-cell syte-experiment-card-name-cell'>
          <div className='syte-experiment-card-cell-field'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
              {experiment.name}
            </Typography>
            <Typography
              type={TypographyType.Body}
              variant={TypographyVariant.SmallRegular}
              className='syte-experiment-card-name-meta'
            >
              {nameMeta}
            </Typography>
          </div>
        </div>

        <div className='syte-experiment-card-cell'>
          <div className='syte-experiment-card-cell-label'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Metric
            </Typography>
          </div>
          <div className='syte-experiment-card-cell-field'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              {experimentMetricTranslations[experiment.metric]}
            </Typography>
          </div>
        </div>

        <div className='syte-experiment-card-cell'>
          <div className='syte-experiment-card-cell-label'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Status
            </Typography>
          </div>
          <div className='syte-experiment-card-cell-field'>
            <Typography
              type={TypographyType.Body}
              variant={TypographyVariant.SmallRegular}
              className={winnerStatus.className}
            >
              {winnerStatus.text}
            </Typography>
          </div>
        </div>

        <div className='syte-experiment-card-cell'>
          <div className='syte-experiment-card-cell-label'>
            <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
              Lift
            </Typography>
          </div>
          <div className='syte-experiment-card-cell-field'>
            <Typography
              type={TypographyType.Body}
              variant={TypographyVariant.SmallRegular}
              className={liftClassName}
            >
              {liftText}
            </Typography>
          </div>
        </div>

        <div className='syte-experiment-card-cell'>{actionButtons}</div>

        {showDeleteDialog && (
          <ConfirmationDialog onCancel={onDeleteDialogClose}>
            <ConfirmationDialog.Header>
              <ConfirmationDialog.Title>Hold on</ConfirmationDialog.Title>
            </ConfirmationDialog.Header>
            <ConfirmationDialog.Content>
              Are you sure you want to delete this experiment?
            </ConfirmationDialog.Content>
            <ConfirmationDialog.Footer>
              <ButtonsContainer>
                <Button variant='primary' onClick={onDeleteExperiment}>
                  Delete
                </Button>
                <Button variant='outlined' onClick={() => setShowDeleteDialog(false)}>
                  Cancel
                </Button>
              </ButtonsContainer>
            </ConfirmationDialog.Footer>
          </ConfirmationDialog>
        )}
        {showUseUuidDialog && (
          <UseUuidModal
            onRunExperiment={onRunExperiment}
            setShowUseUuidDialog={setShowUseUuidDialog}
          />
        )}
      </div>
    </CardItem>
  );
};
